import { useState } from 'react'
import type { FC } from 'react'
import Collapse from '@mui/material/Collapse'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import ExpandMore from '@mui/icons-material/ExpandMore'
import ExpandLess from '@mui/icons-material/ExpandLess'
import Typography from '@mui/material/Typography'
import { FFModal, FileUpload, Loader, type FileWithType } from '~/shared/ui'
import { userCoverageSubmitInsuranceInfo } from '@fireflyhealth/api'
import { queueNotification } from '~/shared/notifications/actions'
import { patientsSlice } from '~/entities/person'
import { useAppDispatch } from '~/app/store/hooks'
import { fireAndForget } from '~/shared/lib/fireAndForget'
import { logger } from '~/shared/lib/logger'

const INSURANCE_CARD_IMAGE_SX = { width: '100%', height: 'auto', borderRadius: '8px' }

interface EditPatientInsuranceCardProps {
  /**
   * Only the user id and the person's two card images are read. Typed structurally rather
   * than as `PatientUser` because the sole caller assembles this from a person plus an
   * optional parent user — `id` is absent in the person-only flow, which is why submitting
   * is gated on it below.
   */
  patient: {
    id: number | undefined
    person?: {
      insurance_info?: {
        image_front?: string | null
        image_back?: string | null
      } | null
    } | null
  }
}

const EditPatientInsuranceCard: FC<EditPatientInsuranceCardProps> = props => {
  const dispatch = useAppDispatch()
  const { palette } = useTheme()
  const [insuranceFrontImage, setInsuranceFrontImage] = useState(
    props.patient.person?.insurance_info?.image_front,
  )
  const [insuranceBackImage, setInsuranceBackImage] = useState(
    props.patient.person?.insurance_info?.image_back,
  )
  // What FileUpload hands back for a pending selection, before it is submitted.
  const [uploadedInsuranceFrontImage, setUploadedInsuranceFrontImage] =
    useState<FileWithType | null>(null)
  const [uploadedInsuranceBackImage, setUploadedInsuranceBackImage] = useState<FileWithType | null>(
    null,
  )
  const [uploadKey, setUploadKey] = useState<number>(0)
  const [isInsuranceFrontImageChanged, setIsInsuranceFrontImageChanged] = useState<boolean>(false)
  const [isInsuranceBackImageChanged, setIsInsuranceBackImageChanged] = useState<boolean>(false)
  const [isUpdatingInsuranceImage, setIsUpdatingInsuranceImage] = useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [visible, setVisible] = useState<boolean>(false)
  const uploadFile = async () => {
    const userId = props.patient.id
    if (userId == null) {
      return
    }
    setIsUpdatingInsuranceImage(true)
    try {
      const formData = new FormData()
      if (uploadedInsuranceFrontImage != null) {
        formData.append('image_front', uploadedInsuranceFrontImage.data)
      }
      if (uploadedInsuranceBackImage != null) {
        formData.append('image_back', uploadedInsuranceBackImage.data)
      }
      const { data: user } = await userCoverageSubmitInsuranceInfo.create(
        { id: userId },
        { data: formData },
      )
      // Update the images
      setInsuranceFrontImage(user.person?.insurance_info?.image_front)
      setInsuranceBackImage(user.person?.insurance_info?.image_back)
      fireAndForget('[Patient] refetch patient', () =>
        dispatch(patientsSlice.thunks.getPatient(userId)),
      )
      dispatch(
        queueNotification({
          variant: 'success',
          message: 'Updated insurance card',
        }),
      )
    } catch (e) {
      logger.error('[Patient] upload insurance card', e)
      dispatch(
        queueNotification({
          variant: 'error',
          message: `Failed to update insurance card`,
        }),
      )
    } finally {
      // reset the values for the upload widget
      setUploadedInsuranceFrontImage(null)
      setUploadedInsuranceBackImage(null)
      setIsInsuranceFrontImageChanged(false)
      setIsInsuranceBackImageChanged(false)
      setUploadKey(uploadKey + 1)
      setIsUpdatingInsuranceImage(false)
      setIsModalOpen(false)
    }
  }
  const openModal = () => {
    setIsModalOpen(true)
  }
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="h6">Insurance Card</Typography>
        <Box sx={{ ml: 1 }}>
          <IconButton onClick={() => setVisible(!visible)}>
            {visible ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>
      {
        <Collapse in={visible}>
          <Box sx={{ display: 'flex', mt: 1, flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', mt: 1, mb: 2 }}>
              <Box
                key="front"
                sx={{
                  flex: 1,
                  p: 1,
                  mr: '1',
                  border: `0.1rem solid ${palette.grey[200]}`,
                  borderRadius: 8,
                }}
              >
                <Typography>Insurance Card Front</Typography>
                {insuranceFrontImage && (
                  <a href={insuranceFrontImage} target="_blank" rel="noopener noreferrer">
                    <Box
                      component="img"
                      sx={INSURANCE_CARD_IMAGE_SX}
                      src={insuranceFrontImage}
                      alt="Insurance Card Front"
                    />
                  </a>
                )}
                <Box>
                  <FileUpload
                    disabled={isUpdatingInsuranceImage}
                    file={uploadedInsuranceFrontImage}
                    key={uploadKey}
                    handleFile={fileWithType => {
                      setIsInsuranceFrontImageChanged(true)
                      setUploadedInsuranceFrontImage(fileWithType)
                    }}
                    handleClear={() => {
                      setIsInsuranceFrontImageChanged(false)
                      setInsuranceFrontImage(props.patient.person?.insurance_info?.image_front)
                      setUploadedInsuranceFrontImage(null)
                      setUploadKey(uploadKey + 1)
                    }}
                    supportedImageTypes={['image/jpeg', 'image/png', 'image/tiff']}
                    supportedDocTypes={[]}
                    supportMessage="Supports JPG, PNG, or TIFF"
                  />
                  {isInsuranceFrontImageChanged && uploadedInsuranceFrontImage && (
                    <Typography>{uploadedInsuranceFrontImage.data.name}</Typography>
                  )}
                </Box>
              </Box>
              <Box
                key="back"
                sx={{
                  flex: 1,
                  p: 1,
                  mr: '0',
                  border: `0.1rem solid ${palette.grey[200]}`,
                  borderRadius: 8,
                }}
              >
                <Typography>Insurance Card Back</Typography>
                {insuranceBackImage && (
                  <a href={insuranceBackImage} target="_blank" rel="noopener noreferrer">
                    <Box
                      component="img"
                      sx={INSURANCE_CARD_IMAGE_SX}
                      src={insuranceBackImage}
                      alt="Insurance Card Back"
                    />
                  </a>
                )}
                <Box>
                  <FileUpload
                    disabled={isUpdatingInsuranceImage}
                    file={uploadedInsuranceBackImage}
                    key={uploadKey}
                    handleFile={fileWithType => {
                      setIsInsuranceBackImageChanged(true)
                      setUploadedInsuranceBackImage(fileWithType)
                    }}
                    handleClear={() => {
                      setIsInsuranceBackImageChanged(false)
                      setInsuranceBackImage(props.patient.person?.insurance_info?.image_back)
                      setUploadedInsuranceBackImage(null)
                      setUploadKey(uploadKey + 1)
                    }}
                    supportedImageTypes={['image/jpeg', 'image/png', 'image/tiff']}
                    supportedDocTypes={[]}
                    supportMessage="Supports JPG, PNG, or TIFF"
                  />
                  {isInsuranceBackImageChanged && uploadedInsuranceBackImage && (
                    <Typography>{uploadedInsuranceBackImage.data.name}</Typography>
                  )}
                </Box>
              </Box>
            </Box>
            <Grid container spacing={1} sx={{ justifyContent: 'flex-end' }}>
              <Button
                color="primary"
                onClick={openModal}
                variant="outlined"
                disabled={
                  !isInsuranceFrontImageChanged ||
                  !isInsuranceBackImageChanged ||
                  isUpdatingInsuranceImage ||
                  props.patient.id == null
                }
              >
                {isUpdatingInsuranceImage ? <Loader inline /> : 'Update'}
              </Button>
            </Grid>
            {!isModalOpen ? null : (
              <FFModal
                open={true}
                header={
                  <Box sx={{ m: 2 }}>
                    <Typography variant="h5">Update Insurance Card Images</Typography>
                  </Box>
                }
                footer={
                  <Box sx={{ m: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      onClick={() => setIsModalOpen(false)}
                      disabled={isUpdatingInsuranceImage}
                    >
                      Cancel
                    </Button>
                  </Box>
                }
              >
                <>
                  <Box sx={{ m: 2 }}>
                    <Typography>
                      Are you sure you want to update the insurance cards? After the update,
                      eligibility checks will automatically run on the new images.
                    </Typography>
                  </Box>
                  <Box sx={{ m: 2 }}>
                    <Button
                      onClick={() => void uploadFile()}
                      disabled={isUpdatingInsuranceImage}
                      variant="contained"
                    >
                      Yes, Update
                    </Button>
                  </Box>
                </>
              </FFModal>
            )}
          </Box>
        </Collapse>
      }
    </>
  )
}

export default EditPatientInsuranceCard
